import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import EventFormFields from "@/components/EventFormFields";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const EMPTY = { title: "", date: "", end_date: "", time: "", end_time: "", commune: "", description: "", image_url: "", category: "" };

export default function PartnerEventForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const backTo = isAdmin ? "/admin" : "/partner";
  const backLabel = isAdmin ? "Retour au tableau de bord" : "Retour à mon espace";

  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEdit);

  useEffect(() => {
    if (isEdit) {
      api.get(`/events/${id}`)
        .then((r) => {
          const e = r.data;
          setForm({
            title: e.title, date: e.date, end_date: e.end_date || "",
            time: e.time, end_time: e.end_time || "", commune: e.commune,
            description: e.description, image_url: e.image_url, category: e.category,
          });
        })
        .catch((err) => toast.error(formatApiError(err)))
        .finally(() => setInitialLoading(false));
    }
  }, [id, isEdit]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.category) { toast.error("Veuillez choisir une catégorie"); return; }
    if (form.end_date && form.end_date < form.date) {
      toast.error("La date de fin doit être après la date de début");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        end_date: form.end_date || null,
        end_time: form.end_time || null,
      };
      if (isEdit) {
        await api.put(`/events/${id}`, payload);
        toast.success(isAdmin
          ? "Événement mis à jour."
          : "Événement mis à jour. Il sera de nouveau soumis à modération.");
      } else {
        await api.post("/events", payload);
        toast.success(isAdmin
          ? "Événement publié."
          : "Événement soumis ! Il sera publié après validation.");
      }
      navigate(backTo);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) return <div className="p-10 text-center text-muted_text">Chargement…</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 fade-in">
      <Link to={backTo} className="inline-flex items-center gap-1.5 text-sm text-muted_text hover:text-sage mb-6">
        <ArrowLeft className="w-4 h-4" /> {backLabel}
      </Link>

      <h1 className="font-heading font-bold text-3xl sm:text-4xl text-ink mb-2">
        {isEdit ? "Modifier l'événement" : "Nouvel événement"}
      </h1>
      <p className="text-muted_text mb-8">
        {isAdmin
          ? "En tant qu'administrateur, votre événement sera publié immédiatement, sans validation."
          : isEdit
            ? "Les modifications nécessitent une nouvelle validation."
            : "Votre événement apparaîtra publiquement après validation par un administrateur."}
      </p>

      <form onSubmit={onSubmit} className="bg-white rounded-2xl border border-[#F0E0CC] p-6 sm:p-8" data-testid="event-form">
        <EventFormFields form={form} setForm={setForm} />
        <div className="mt-8 flex gap-3">
          <Button type="submit" disabled={loading}
            className="rounded-xl bg-sage hover:bg-sage-dark text-white h-11 px-6 font-medium"
            data-testid="submit-event-button">
            {loading ? "Envoi…" : isEdit ? "Mettre à jour" : isAdmin ? "Publier l'événement" : "Soumettre l'événement"}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate(backTo)}
            className="rounded-xl border-[#F0E0CC] h-11 px-6">
            Annuler
          </Button>
        </div>
      </form>
    </div>
  );
}
